import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import { AppointmentType, APPOINTMENT_TYPES, getAppointmentTypeLabel } from '../constants/appointmentTypes';

interface Currency { code: string; name: string; }

interface Client {
  client_id: string;
  client_name: string;
  client_email: string;
}

interface Props {
  clients: Client[];
  onClose: () => void;
  onSaved: () => void;
  coachCurrency?: string;
}

const DURATIONS = [30, 45, 60, 75, 90, 120];
const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
] as const;
type Frequency = (typeof FREQUENCIES)[number]['value'];

function previewSessionCount(
  startDate: string,
  endDate: string,
  frequency: Frequency,
): number | null {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return null;

  let count = 0;
  let current = new Date(start);
  while (current <= end) {
    count++;
    if (frequency === 'weekly') {
      current.setDate(current.getDate() + 7);
    } else if (frequency === 'biweekly') {
      current.setDate(current.getDate() + 14);
    } else {
      current.setMonth(current.getMonth() + 1);
    }
  }
  return count;
}

export default function LogPastSessionModal({ clients, onClose, onSaved, coachCurrency }: Props) {
  const today = new Date().toISOString().slice(0, 10);

  const [mode, setMode] = useState<'single' | 'recurring'>('single');

  // Shared fields
  const [clientId, setClientId] = useState('');
  const [time, setTime] = useState('09:00');
  const [durationPreset, setDurationPreset] = useState<number | 'custom'>(60);
  const [customDuration, setCustomDuration] = useState('');
  const [meetingName, setMeetingName] = useState('');
  const [notes, setNotes] = useState('');
  const [apptType, setApptType] = useState<AppointmentType>('billable');
  const [barterNote, setBarterNote] = useState('');
  const [rate, setRate] = useState('');
  const [currency, setCurrency] = useState(coachCurrency?.toLowerCase() || 'usd');
  const [currencies, setCurrencies] = useState<Currency[]>([{ code: 'usd', name: 'USD' }]);

  useEffect(() => {
    fetch(`${config.apiBaseUrl}/countries`)
      .then(r => r.ok ? r.json() : [])
      .then((countries: any[]) => {
        const seen = new Set<string>();
        const unique = countries
          .filter((c: any) => { if (seen.has(c.currency_code)) return false; seen.add(c.currency_code); return true; })
          .map((c: any) => ({ code: c.currency_code.toLowerCase(), name: c.currency_code.toUpperCase() }))
          .sort((a: Currency, b: Currency) => a.name.localeCompare(b.name));
        if (unique.length > 0) setCurrencies(unique);
      })
      .catch(() => {});
  }, []);
  const [paymentStatus, setPaymentStatus] = useState<'paid' | 'unpaid' | 'waived'>('paid');
  const [paymentNote, setPaymentNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Single-session field
  const [date, setDate] = useState('');

  // Recurring fields
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [frequency, setFrequency] = useState<Frequency>('weekly');

  const durationMinutes =
    durationPreset === 'custom' ? parseInt(customDuration || '0', 10) : durationPreset;

  const sortedClients = [...clients.filter(c => c.client_id)].sort((a, b) =>
    a.client_name.localeCompare(b.client_name),
  );

  const sessionPreview = useMemo(
    () => (mode === 'recurring' ? previewSessionCount(startDate, endDate, frequency) : null),
    [mode, startDate, endDate, frequency],
  );

  const oneYearFromStart = useMemo(() => {
    if (!startDate) return today;
    const d = new Date(startDate + 'T00:00:00');
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  }, [startDate, today]);

  async function handleSave() {
    setError('');
    if (!clientId) { setError('Please select a client.'); return; }
    if (!durationMinutes || durationMinutes < 1) { setError('Please enter a valid duration.'); return; }

    if (mode === 'single') {
      if (!date) { setError('Please select a date.'); return; }
      await saveSingle();
    } else {
      if (!startDate) { setError('Please select a start date.'); return; }
      if (!endDate) { setError('Please select an end date.'); return; }
      if (endDate < startDate) { setError('End date must be on or after start date.'); return; }
      await saveRecurring();
    }
  }

  async function saveSingle() {
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken') || '';
      const body: Record<string, unknown> = {
        client_id: clientId,
        date,
        time,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        duration_minutes: durationMinutes,
        meeting_name: meetingName.trim(),
        appointment_type: apptType,
        barter_note: barterNote.trim(),
        notes: notes.trim(),
        currency,
        payment_status: paymentStatus,
        payment_note: paymentNote.trim(),
      };
      if (apptType === 'billable' && rate) body.rate = parseFloat(rate);

      const resp = await fetch(`${config.apiBaseUrl}/coaches/sessions/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error((err as { detail?: string }).detail || 'Failed to save session.');
      }
      onSaved();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save session.');
    } finally {
      setSaving(false);
    }
  }

  async function saveRecurring() {
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken') || '';
      const body: Record<string, unknown> = {
        client_id: clientId,
        recurring: { start_date: startDate, end_date: endDate, frequency },
        time,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        duration_minutes: durationMinutes,
        meeting_name: meetingName.trim(),
        appointment_type: apptType,
        barter_note: barterNote.trim(),
        notes: notes.trim(),
        currency,
        payment_status: paymentStatus,
        payment_note: paymentNote.trim(),
      };
      if (apptType === 'billable' && rate) body.rate = parseFloat(rate);

      const resp = await fetch(`${config.apiBaseUrl}/coaches/sessions/log-recurring`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error((err as { detail?: string }).detail || 'Failed to save sessions.');
      }
      onSaved();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save sessions.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-dark-card border border-dark-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-dark-border">
          <h2 className="text-white font-semibold text-base">Log Past Session</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Mode toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('single')}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                mode === 'single'
                  ? 'bg-primary-600 text-white'
                  : 'bg-dark-canvas border border-dark-border text-gray-300 hover:border-gray-500'
              }`}
            >
              Single session
            </button>
            <button
              type="button"
              onClick={() => setMode('recurring')}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                mode === 'recurring'
                  ? 'bg-primary-600 text-white'
                  : 'bg-dark-canvas border border-dark-border text-gray-300 hover:border-gray-500'
              }`}
            >
              Recurring sessions
            </button>
          </div>

          {/* ── Session details ── */}
          <div className="space-y-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Session Details</p>

            {/* Client */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Client <span className="text-red-400">*</span></label>
              <select
                value={clientId}
                onChange={e => setClientId(e.target.value)}
                className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-500 [color-scheme:dark]"
              >
                <option value="">Select a client…</option>
                {sortedClients.map(c => (
                  <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                ))}
              </select>
            </div>

            {mode === 'single' ? (
              /* Single: date + time */
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Date <span className="text-red-400">*</span></label>
                  <input
                    type="date"
                    value={date}
                    max={today}
                    onChange={e => setDate(e.target.value)}
                    className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-500 [color-scheme:dark]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Time</label>
                  <input
                    type="time"
                    value={time}
                    onChange={e => setTime(e.target.value)}
                    className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-500 [color-scheme:dark]"
                  />
                </div>
              </div>
            ) : (
              /* Recurring: date range + frequency + time */
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Start date <span className="text-red-400">*</span></label>
                    <input
                      type="date"
                      value={startDate}
                      max={today}
                      onChange={e => { setStartDate(e.target.value); if (endDate && e.target.value > endDate) setEndDate(''); }}
                      className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-500 [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">End date <span className="text-red-400">*</span></label>
                    <input
                      type="date"
                      value={endDate}
                      min={startDate || undefined}
                      max={oneYearFromStart}
                      onChange={e => setEndDate(e.target.value)}
                      className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-500 [color-scheme:dark]"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Frequency</label>
                    <select
                      value={frequency}
                      onChange={e => setFrequency(e.target.value as Frequency)}
                      className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-500 [color-scheme:dark]"
                    >
                      {FREQUENCIES.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Time</label>
                    <input
                      type="time"
                      value={time}
                      onChange={e => setTime(e.target.value)}
                      className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-500 [color-scheme:dark]"
                    />
                  </div>
                </div>
                {sessionPreview !== null && (
                  <p className="text-xs text-primary-400 font-medium">
                    This will create {sessionPreview} session{sessionPreview !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            )}

            {/* Duration */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Duration <span className="text-red-400">*</span></label>
              <div className="flex gap-2 flex-wrap">
                {DURATIONS.map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDurationPreset(d)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      durationPreset === d
                        ? 'bg-primary-600 text-white'
                        : 'bg-dark-canvas border border-dark-border text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    {d} min
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setDurationPreset('custom')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    durationPreset === 'custom'
                      ? 'bg-primary-600 text-white'
                      : 'bg-dark-canvas border border-dark-border text-gray-300 hover:border-gray-500'
                  }`}
                >
                  Custom
                </button>
                {durationPreset === 'custom' && (
                  <input
                    type="number"
                    min="1"
                    max="480"
                    placeholder="minutes"
                    value={customDuration}
                    onChange={e => setCustomDuration(e.target.value)}
                    className="w-24 bg-dark-canvas border border-dark-border rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-primary-500"
                  />
                )}
              </div>
            </div>

            {/* Session name */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Session name</label>
              <input
                type="text"
                placeholder="Coaching Session"
                value={meetingName}
                onChange={e => setMeetingName(e.target.value)}
                className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-500"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Notes <span className="text-gray-600 font-normal">(coach-only)</span></label>
              <textarea
                rows={3}
                placeholder="Key topics, progress, observations…"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-500 resize-none"
              />
            </div>
          </div>

          {/* ── Billing ── */}
          <div className="space-y-4 border-t border-dark-border pt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Billing</p>

            {/* Type */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Session type</label>
              <div className="flex gap-2">
                {APPOINTMENT_TYPES.map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setApptType(t)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      apptType === t
                        ? 'bg-primary-600 text-white'
                        : 'bg-dark-canvas border border-dark-border text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    {getAppointmentTypeLabel(t)}
                  </button>
                ))}
              </div>
            </div>

            {apptType === 'barter' && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Barter note</label>
                <input
                  type="text"
                  placeholder="e.g. Design work in exchange"
                  value={barterNote}
                  onChange={e => setBarterNote(e.target.value)}
                  className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-500"
                />
              </div>
            )}

            {apptType === 'billable' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Amount{mode === 'recurring' && <span className="text-gray-600 font-normal"> (per session)</span>}</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="150.00"
                        value={rate}
                        onChange={e => setRate(e.target.value)}
                        className="flex-1 bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-500"
                      />
                      <select
                        value={currency}
                        onChange={e => setCurrency(e.target.value)}
                        className="border border-dark-border rounded-lg px-2 py-2 text-sm bg-dark-canvas text-gray-200 focus:outline-none [color-scheme:dark]"
                      >
                        {currencies.map(c => (
                          <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Payment status</label>
                    <select
                      value={paymentStatus}
                      onChange={e => setPaymentStatus(e.target.value as 'paid' | 'unpaid' | 'waived')}
                      className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-500 [color-scheme:dark]"
                    >
                      <option value="paid">Paid</option>
                      <option value="unpaid">Unpaid</option>
                      <option value="waived">Waived</option>
                    </select>
                  </div>
                </div>

                {paymentStatus === 'paid' && (
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Payment method</label>
                    <input
                      type="text"
                      placeholder="e.g. Cash, Venmo, Check"
                      value={paymentNote}
                      onChange={e => setPaymentNote(e.target.value)}
                      className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-500"
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {error && (
            <p className="text-red-400 text-xs">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-dark-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {saving
              ? 'Saving…'
              : mode === 'recurring' && sessionPreview
              ? `Save ${sessionPreview} Session${sessionPreview !== 1 ? 's' : ''}`
              : 'Save Session'}
          </button>
        </div>
      </div>
    </div>
  );
}
